"use client"

import { CheckCircle2, AlertCircle, Loader2, Clock, ChevronRight } from "lucide-react"
import { cn } from "@/lib/utils"

interface ToolResultCardProps {
  toolName: string
  status: "executing" | "Completed" | "error" | "confirming"
  success?: boolean
  summary?: string
}

const toolDisplayNames: Record<string, string> = {
  recipient_search: "Searching recipients",
  recipient_filter: "Filtering recipients",
  recipient_get: "Looking up recipient",
  recipient_create: "Creating recipient",
  recipient_update: "Updating recipient",
  recipient_delete: "Deleting recipient",
  recipient_metrics: "Calculating metrics",
  group_list: "Listing groups",
  group_get: "Looking up group",
  group_create: "Creating group",
  group_update: "Updating group",
  group_delete: "Deleting group",
  group_manage_members: "Managing members",
  group_merge: "Merging groups",
  smart_group_list: "Listing smart groups",
  smart_group_get: "Looking up smart group",
  smart_group_create: "Creating smart group",
  smart_group_toggle_status: "Toggling status",
  smart_group_duplicate: "Duplicating smart group",
  smart_group_delete: "Deleting smart group",
  tag_list: "Listing tags",
  tag_create: "Creating tag",
  tag_update: "Updating tag",
  tag_delete: "Deleting tag",
  tag_merge: "Merging tags",
  field_list: "Listing custom fields",
  field_create: "Creating field",
  field_update: "Updating field",
  field_delete: "Deleting field",
  suppression_list: "Checking suppression list",
  suppression_add: "Adding to suppression",
  suppression_remove: "Removing from suppression",
  suppression_check: "Checking suppression",
  audit_query: "Querying audit log",
  audit_summary: "Getting audit summary",
  analytics_recipient_metrics: "Calculating metrics",
  analytics_data_quality: "Analyzing data quality",
  analytics_filter_options: "Getting filter options",
  group_preview_rules: "Previewing rules",
  smart_group_update: "Updating smart group",
  smart_group_templates: "Loading templates",
  smart_group_activity: "Loading activity",
  integration_status: "Checking integrations",
  navigate_to_page: "Navigating to page",
}

export function ToolResultCard({ toolName, status, success, summary }: ToolResultCardProps) {
  const displayName = toolDisplayNames[toolName] || toolName.replace(/_/g, " ")

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 py-1 text-xs min-w-0",
        status === "executing" && "text-muted-foreground",
        status === "Completed" && success && "text-muted-foreground",
        status === "Completed" && !success && "text-destructive/70",
        status === "error" && "text-destructive/70",
        status === "confirming" && "text-yellow-600 dark:text-yellow-400"
      )}
    >
      {status === "executing" && <Loader2 className="h-3 w-3 animate-spin shrink-0" />}
      {status === "Completed" && success && (
        <CheckCircle2 className="h-3 w-3 text-primary/60 shrink-0" />
      )}
      {(status === "error" || (status === "Completed" && !success)) && (
        <AlertCircle className="h-3 w-3 shrink-0" />
      )}
      {status === "confirming" && <Clock className="h-3 w-3 shrink-0" />}

      <span className={cn(status === "executing" && "animate-pulse")}>
        {status === "executing" ? displayName : displayName.replace(/ing\b/, "ed").replace(/Runn/, "Ran")}
      </span>
      {summary && (
        <>
          <ChevronRight className="h-2.5 w-2.5 shrink-0 opacity-40" />
          <span className="truncate opacity-70">{summary}</span>
        </>
      )}
    </div>
  )
}
